import {
    createMainPeerConnection,
    isTransceiverInit,
    type MainPeerConnectionOptions,
    type TransceiverConfig,
} from '@pexip/peer-connection';

import type {PCCallStrategy} from './call';
import {createCallWithReceivers} from './call';
import type {ClientCallType} from './clientCallType';
import {
    DEFAULT_CLIENT_CALLTYPE,
    MAX_RECEIVE_AUDIO_STREAMS,
    MAX_RECEIVE_VIDEO_STREAMS,
    MIX_PARTICIPANT_UUID,
} from './constants';
import {logger} from './logger';
import {isParticipantID} from './typeGuards';
import type {
    Call,
    CallOptions,
    MID,
    ParticipantID,
    SwitchedStreamsEvent,
    VideoQuality,
} from './types';
import {
    createSerialQueue,
    isReceivingAudio,
    isReceivingVideo,
    isSendingAudio,
    isSendingVideo,
} from './utils';

const createRecvTransceivers = (
    kind: 'audio' | 'video',
    length: number,
    content?: string,
) =>
    Array.from(
        {length},
        () =>
            ({
                direction: 'recvonly',
                kindOrTrack: kind,
                content,
            }) as const,
    );

type MediaKind = 'audio' | 'video';

const MEDIA_KINDS = ['audio', 'video'] as const satisfies MediaKind[];

/** Whether a call type sends each media kind. */
const SENDS: Record<MediaKind, (callType: ClientCallType) => boolean> = {
    audio: isSendingAudio,
    video: isSendingVideo,
};

/** Whether a call type receives each media kind. */
const RECEIVES: Record<MediaKind, (callType: ClientCallType) => boolean> = {
    audio: isReceivingAudio,
    video: isReceivingVideo,
};

/** Number of receive transceivers, and so of received streams, per kind. */
type ReceiveSlots = Record<MediaKind, number>;

/**
 * Parses a configured receive stream count into a whole number from 1 up to
 * `max`. Missing or invalid counts fall back to `max`, larger ones are capped.
 *
 * @example
 * toReceiveSlotCount(20, MAX_RECEIVE_VIDEO_STREAMS); // 8
 */
const toReceiveSlotCount = (requested: number | undefined, max: number) =>
    requested !== undefined && Number.isInteger(requested) && requested >= 1
        ? Math.min(requested, max)
        : max;

/**
 * Parses the configured audio and video receive stream counts of a switching
 * call into its receive slots. A media kind the call type doesn't receive gets
 * no slots.
 *
 * @example
 * toReceiveSlots({numberOfReceiveVideoStreams: 4}); // {audio: 16, video: 4}
 */
const toReceiveSlots = ({
    callType = DEFAULT_CLIENT_CALLTYPE,
    numberOfReceiveAudioStreams,
    numberOfReceiveVideoStreams,
}: Pick<
    CallOptions,
    'callType' | 'numberOfReceiveAudioStreams' | 'numberOfReceiveVideoStreams'
>): ReceiveSlots => ({
    audio: RECEIVES.audio(callType)
        ? toReceiveSlotCount(
              numberOfReceiveAudioStreams,
              MAX_RECEIVE_AUDIO_STREAMS,
          )
        : 0,
    video: RECEIVES.video(callType)
        ? toReceiveSlotCount(
              numberOfReceiveVideoStreams,
              MAX_RECEIVE_VIDEO_STREAMS,
          )
        : 0,
});

// -----------------------------------------------------------------------------
// createSFUPeerConnection — top-level, pure function of its arguments
// -----------------------------------------------------------------------------

interface CreateSFUPeerConnectionOptions {
    pcMainSignals: CallOptions['pcMainSignals'];
    peerOptions: MainPeerConnectionOptions;
    callType: ClientCallType;
    mediaStream: MediaStream | undefined;
    polite?: boolean;
    rtcConfig?: RTCConfiguration;
    receiveSlots: ReceiveSlots;
}

const createSFUPeerConnection = ({
    pcMainSignals,
    peerOptions,
    callType,
    mediaStream,
    polite,
    receiveSlots,
    rtcConfig = peerOptions.rtcConfig,
}: CreateSFUPeerConnectionOptions) => {
    const [audioTrack] = mediaStream?.getAudioTracks() ?? [];
    const [videoTrack] = mediaStream?.getVideoTracks() ?? [];
    const streams = mediaStream && [mediaStream];

    // Only the media kinds the call type sends get a send transceiver, so a
    // receive-only call never sends local tracks.
    const sentKinds = MEDIA_KINDS.filter(kind => SENDS[kind](callType));
    const sendInits = sentKinds.map(kind => {
        const track = kind === 'audio' ? audioTrack : videoTrack;
        return {
            content: 'main' as const,
            direction: 'sendonly' as const,
            kindOrTrack: track ?? kind,
            ...(streams && {streams}),
            ...(kind === 'video' && {
                sendEncodings: [
                    {
                        rid: 'h',
                        scalabilityMode: 'L1T1',
                    },
                    {
                        rid: 'm',
                        scalabilityMode: 'L1T1',
                        scaleResolutionDownBy: 2,
                    },
                    {
                        rid: 'l',
                        scalabilityMode: 'L1T1',
                        scaleResolutionDownBy: 4,
                    },
                ],
            }),
        };
    });

    return createMainPeerConnection(pcMainSignals, {
        ...peerOptions,
        polite,
        rtcConfig,
        mediaInits: [
            ...sendInits,
            ...createRecvTransceivers('audio', receiveSlots.audio),
            ...createRecvTransceivers('video', receiveSlots.video),
        ].flatMap(init => (isTransceiverInit(init) ? [init] : [])),
    });
};

// -----------------------------------------------------------------------------
// SFU (switching) Strategy
// -----------------------------------------------------------------------------

/**
 * Creates an SFU-specific strategy for peer connection call lifecycle.
 *
 * Handles the switching call type where the MCU acts as an SFU, forwarding
 * individual participant streams rather than compositing them.
 *
 * @example
 * ```ts
 * const strategy = createSFUStrategy(options);
 * const call = createCall(options, strategy);
 * ```
 */
const createSFUStrategy = (options: CallOptions): PCCallStrategy => {
    const {
        pcMainSignals,
        peerOptions,
        callType = DEFAULT_CLIENT_CALLTYPE,
        mediaStream,
        callSignals,
    } = options;

    const pcOptions = {
        pcMainSignals,
        peerOptions,
        callType,
        mediaStream,
        receiveSlots: toReceiveSlots(options),
    } as const;

    /**
     * The send transceivers of the current peer connection, the only ones
     * `setStream` attaches the local stream to. They are recorded when the
     * peer connection is created, because renegotiation can later change the
     * direction of any transceiver.
     */
    let sendConfigs = new WeakSet<TransceiverConfig>();

    return {
        createPC: (rtcConfig, polite) => {
            const peer = createSFUPeerConnection({
                ...pcOptions,
                rtcConfig,
                polite,
            });
            sendConfigs = new WeakSet(
                peer
                    .getTransceiverConfigs()
                    .filter(config => config.direction !== 'recvonly'),
            );
            return peer;
        },

        handleRemoteStreams: transceiverConfig => {
            if (transceiverConfig.remoteStreams) {
                for (const stream of transceiverConfig.remoteStreams) {
                    callSignals.onRemoteStream.emit(stream);
                }
            }
        },

        setupStats: () => undefined,
        cleanupStats: () => undefined,
        resetStats: () => [],
        wireStatsSignals: () => [],
        isLocalStreamTarget: config => sendConfigs.has(config),
    };
};

// -----------------------------------------------------------------------------
// createSFUCall — public entry point for SFU (switching) calls
// -----------------------------------------------------------------------------

const SPEAKING_THRESHOLD = 0.01;
const SPEAKING_POLL_MS = 200;
/** Passes that try to release a MID before it is forgotten anyway. */
const MAX_RELEASE_ATTEMPTS = 3;

type ParticipantsByKind = Record<MediaKind, Set<ParticipantID>>;

const isMediaKind = (value: unknown): value is MediaKind =>
    MEDIA_KINDS.some(kind => kind === value);

/**
 * Parses a `switched_streams` snapshot into the media kinds that each remote
 * participant offers. Only main streams with exactly one contributing
 * participant are per-participant streams; presentation streams, multi-source
 * streams, invalid participant IDs and the local participant are ignored.
 *
 * @example
 * ```ts
 * const available = toAvailableParticipantMedia(event, localUuid);
 * available.video.has(remoteUuid);
 * ```
 */
const toAvailableParticipantMedia = (
    event: SwitchedStreamsEvent,
    localParticipantUuid?: ParticipantID,
): ParticipantsByKind => {
    const available: ParticipantsByKind = {audio: new Set(), video: new Set()};
    for (const stream of Object.values(event)) {
        const contributors: unknown = stream.contributing_participants;
        if (
            stream.stream_role !== 'main' ||
            !isMediaKind(stream.stream_type) ||
            !Array.isArray(contributors) ||
            contributors.length !== 1
        ) {
            continue;
        }
        const [participantId] = contributors;
        if (
            typeof participantId !== 'string' ||
            !isParticipantID(participantId) ||
            participantId === localParticipantUuid
        ) {
            continue;
        }
        available[stream.stream_type].add(participantId);
    }
    return available;
};

/**
 * Chooses the participant videos to receive: every announced one while they
 * fit the video slots, otherwise the transcoded mix. A call without video slots
 * receives no video.
 */
const toRequestedVideo = (
    available: Set<ParticipantID>,
    videoSlots: number,
): Set<ParticipantID> => {
    if (videoSlots === 0) {
        return new Set();
    }
    return available.size > videoSlots
        ? new Set([MIX_PARTICIPANT_UUID])
        : available;
};

/**
 * Chooses the streams to receive for the announced media: each participant's
 * audio up to the audio slots, and each participant's video while it fits the
 * video slots. With more participants sending video than there are video
 * slots, the transcoded mix replaces the per-participant video.
 *
 * @example
 * ```ts
 * toRequestedMedia(available, {audio: 16, video: 8}).video;
 * // available.video, or {MIX_PARTICIPANT_UUID} beyond 8 participants
 * ```
 */
const toRequestedMedia = (
    available: ParticipantsByKind,
    slots: ReceiveSlots,
): ParticipantsByKind => ({
    audio: new Set([...available.audio].slice(0, slots.audio)),
    video: toRequestedVideo(available.video, slots.video),
});

/**
 * Chooses the simulcast layer for each participant video from how many are
 * received: high for one, medium for two to four, low for more. The more
 * videos share the stage, the smaller each one is shown.
 *
 * This is just to exercise video quality selection, logic is more sophisticated later
 *
 * @example
 * toVideoQuality(3); // 'm'
 */
const toVideoQuality = (videoCount: number): VideoQuality => {
    if (videoCount <= 1) {
        return 'h';
    }
    return videoCount <= 4 ? 'm' : 'l';
};

/**
 * Creates an SFU (switching) call.
 *
 * Remote media follows the latest `switched_streams` snapshot: each remote
 * participant's main audio and video are requested when announced and
 * released when no longer announced. When more participants send video than
 * the call has video receive slots, the transcoded mix, keyed by
 * `MIX_PARTICIPANT_UUID`, replaces their video until they fit again. Each
 * participant video is requested at the simulcast layer suited to the number
 * of videos, and requested again when that number crosses a threshold. Video and
 * audio are emitted separately through `onParticipantVideoStreamsUpdate` and
 * `onParticipantAudioStreamsUpdate`.
 *
 * @example
 * ```ts
 * const call = createSFUCall(callOptions);
 * ```
 */
export const createSFUCall = (options: CallOptions): Call => {
    const {
        callSignals,
        eventSignals,
        participantUuid,
        requestAudioStream,
        requestVideoStream,
        releaseAudioStream,
        releaseVideoStream,
    } = options;

    const streamEndpoints = {
        audio: {request: requestAudioStream, release: releaseAudioStream},
        video: {request: requestVideoStream, release: releaseVideoStream},
    } satisfies Record<MediaKind, unknown>;

    const receiveSlots = toReceiveSlots(options);
    const strategy = createSFUStrategy(options);
    const {call, receivers} = createCallWithReceivers(options, strategy, {
        onDisconnect: () => dispose(),
    });

    const assignedMids: Record<MediaKind, Map<ParticipantID, MID>> = {
        audio: new Map(),
        video: new Map(),
    };
    /** Layer each assigned video was requested at; none for the mix. */
    const assignedVideoQualities = new Map<
        ParticipantID,
        VideoQuality | undefined
    >();
    /** Failed attempts to release each MID still assigned. */
    const failedReleases = new Map<MID, number>();
    let availableMedia: ParticipantsByKind = {
        audio: new Set(),
        video: new Set(),
    };
    let videoStreams = new Map<ParticipantID, MediaStream>();
    let audioStreams = new Map<ParticipantID, MediaStream>();
    let activeSpeakers = new Set<ParticipantID>();
    const lifetime = new AbortController();
    const streamSyncQueue = createSerialQueue();

    const requestMid = async (
        kind: MediaKind,
        participantId: ParticipantID,
        preferredQuality?: VideoQuality,
    ) => {
        const request = streamEndpoints[kind].request;
        if (!request) {
            return;
        }
        try {
            const response = await request({
                targetParticipantUuid: participantId,
                ...(preferredQuality && {preferredQuality}),
            });
            // A disposed call must not record streams: a later pass would
            // release them through the endpoints, which address the call
            // manager's current call rather than this one.
            if (lifetime.signal.aborted) {
                return;
            }
            if (response?.status === 200 && response.data.result) {
                assignedMids[kind].set(participantId, response.data.result);
                if (kind === 'video') {
                    assignedVideoQualities.set(participantId, preferredQuality);
                }
            } else {
                logger.warn(
                    {kind, participantId, status: response?.status},
                    'Switched stream request returned no stream',
                );
            }
        } catch (error) {
            logger.warn(
                {error, kind, participantId},
                'Failed to request switched stream',
            );
        }
    };

    /**
     * Releases an assigned MID, and forgets it once released. Until then it
     * may still hold one of the server's receive slots, so a refused or
     * failed release keeps it for a later pass to release again. After
     * `MAX_RELEASE_ATTEMPTS` it is forgotten anyway, so a MID the server will
     * never release, e.g. one it freed itself, cannot hold back requests for
     * good. Resolves whether the MID is no longer held back.
     */
    const releaseMid = async (
        kind: MediaKind,
        participantId: ParticipantID,
        mid: MID,
    ) => {
        let isReleased = false;
        try {
            const response = await streamEndpoints[kind].release?.({
                targetParticipantUuid: participantId,
                mid,
            });
            // No response means there is no call left to release it on.
            isReleased = response === undefined || response.status === 200;
            if (!isReleased) {
                logger.warn(
                    {kind, participantId, mid, status: response?.status},
                    'Switched stream release was refused',
                );
            }
        } catch (error) {
            logger.warn(
                {error, kind, participantId, mid},
                'Failed to release switched stream',
            );
        }
        if (!isReleased) {
            const attempts = (failedReleases.get(mid) ?? 0) + 1;
            if (attempts < MAX_RELEASE_ATTEMPTS) {
                failedReleases.set(mid, attempts);
                return false;
            }
            logger.warn(
                {kind, participantId, mid, attempts},
                'Giving up releasing switched stream',
            );
        }
        failedReleases.delete(mid);
        if (assignedMids[kind].get(participantId) === mid) {
            assignedMids[kind].delete(participantId);
            if (kind === 'video') {
                assignedVideoQualities.delete(participantId);
            }
        }
        return true;
    };

    /**
     * Returns streams for the assigned MIDs, reusing a previous stream while
     * its track is unchanged, or `undefined` when nothing changed.
     */
    const syncStreams = (
        mids: Map<ParticipantID, MID>,
        previous: Map<ParticipantID, MediaStream>,
    ) => {
        const next = new Map<ParticipantID, MediaStream>();
        for (const [participantId, mid] of mids) {
            const track = receivers.getTrackByMid(mid);
            if (!track) {
                continue;
            }
            const current = previous.get(participantId);
            next.set(
                participantId,
                current?.getTracks()[0] === track
                    ? current
                    : new MediaStream([track]),
            );
        }
        const hasChanged =
            next.size !== previous.size ||
            [...next].some(
                ([participantId, stream]) =>
                    previous.get(participantId) !== stream,
            );
        return hasChanged ? next : undefined;
    };

    const emitStreamUpdates = () => {
        const nextVideoStreams = syncStreams(assignedMids.video, videoStreams);
        if (nextVideoStreams) {
            videoStreams = nextVideoStreams;
            callSignals.onParticipantVideoStreamsUpdate.emit(videoStreams);
        }
        const nextAudioStreams = syncStreams(assignedMids.audio, audioStreams);
        if (nextAudioStreams) {
            audioStreams = nextAudioStreams;
            callSignals.onParticipantAudioStreamsUpdate.emit(audioStreams);
        }
    };

    const emitIfAlive = () => {
        if (!lifetime.signal.aborted) {
            emitStreamUpdates();
        }
    };

    const pollActiveSpeakers = () => {
        const nextSpeakers = new Set<ParticipantID>();
        for (const [participantId, mid] of assignedMids.audio) {
            const level = receivers.getAudioLevelByMid(mid);
            if (level !== undefined && level > SPEAKING_THRESHOLD) {
                nextSpeakers.add(participantId);
            }
        }
        if (
            nextSpeakers.size !== activeSpeakers.size ||
            [...nextSpeakers].some(id => !activeSpeakers.has(id))
        ) {
            activeSpeakers = nextSpeakers;
            callSignals.onActiveSpeakersUpdate.emit(activeSpeakers);
        }
    };

    const speakerInterval = setInterval(pollActiveSpeakers, SPEAKING_POLL_MS);

    // Stream requests need the call UUID from the answer to our offer, and
    // `switched_streams` can arrive before it. Every pass queues behind the
    // connection, so it requests from the latest snapshot once the call is up.
    // The observer is added now; the queue only runs its jobs asynchronously.
    const callConnected = new Promise<void>(resolve => {
        const detach = callSignals.onCallConnected.add(
            () => {
                detach();
                resolve();
            },
            {signal: lifetime.signal},
        );
    });
    void streamSyncQueue.enqueue(() => callConnected);

    eventSignals.onSwitchedStreams.add(
        event => {
            availableMedia = toAvailableParticipantMedia(
                event,
                participantUuid,
            );
            // One pass at a time, each converging to the snapshot current when
            // it starts. Releasing first frees receive slots for the requests;
            // failed requests stay unassigned, and failed releases keep their
            // MID and hold back the requests, until a later pass retries them.
            // Each stream is emitted as soon as its request completes, so one
            // slow request doesn't hold back the others. A pass interrupted by
            // `dispose()` requests and emits nothing more.
            void streamSyncQueue.enqueue(async () => {
                // A pass queued before `dispose()` releases nothing.
                if (lifetime.signal.aborted) {
                    return;
                }
                const requested = toRequestedMedia(
                    availableMedia,
                    receiveSlots,
                );
                const videoQuality = requested.video.has(MIX_PARTICIPANT_UUID)
                    ? undefined
                    : toVideoQuality(requested.video.size);
                // Assigned streams that are no longer requested, or video
                // received at another layer, are released before requesting.
                const isOutdated = (
                    kind: MediaKind,
                    participantId: ParticipantID,
                ) =>
                    !requested[kind].has(participantId) ||
                    (kind === 'video' &&
                        assignedVideoQualities.get(participantId) !==
                            videoQuality);
                const releases: Promise<boolean>[] = [];
                for (const kind of MEDIA_KINDS) {
                    for (const [participantId, mid] of [
                        ...assignedMids[kind],
                    ]) {
                        if (isOutdated(kind, participantId)) {
                            releases.push(releaseMid(kind, participantId, mid));
                        }
                    }
                }
                const released = await Promise.all(releases);
                if (lifetime.signal.aborted) {
                    return;
                }
                // An unreleased MID may still hold a receive slot that the
                // requests need.
                if (released.includes(false)) {
                    emitIfAlive();
                    return;
                }
                const requests: Promise<void>[] = [];
                for (const kind of MEDIA_KINDS) {
                    for (const participantId of requested[kind]) {
                        if (!assignedMids[kind].has(participantId)) {
                            requests.push(
                                requestMid(
                                    kind,
                                    participantId,
                                    kind === 'video' ? videoQuality : undefined,
                                ).then(emitIfAlive),
                            );
                        }
                    }
                }
                await Promise.all(requests);
                emitIfAlive();
            });
        },
        {signal: lifetime.signal},
    );

    /**
     * Stops all work and withdraws what this call published, so consumers do
     * not keep its streams or speakers once it ends, for example while a
     * restarted or moved call has no media yet. Signals with nothing published
     * stay silent, so they still emit only on change.
     */
    const dispose = () => {
        lifetime.abort();
        clearInterval(speakerInterval);
        for (const kind of MEDIA_KINDS) {
            assignedMids[kind].clear();
        }
        assignedVideoQualities.clear();
        failedReleases.clear();
        if (videoStreams.size > 0) {
            videoStreams = new Map();
            callSignals.onParticipantVideoStreamsUpdate.emit(videoStreams);
        }
        if (audioStreams.size > 0) {
            audioStreams = new Map();
            callSignals.onParticipantAudioStreamsUpdate.emit(audioStreams);
        }
        if (activeSpeakers.size > 0) {
            activeSpeakers = new Set();
            callSignals.onActiveSpeakersUpdate.emit(activeSpeakers);
        }
    };

    return call;
};
